import { useAuthStore } from "@/stores/auth-store";
import { useUserStore } from "@/stores/user-store";
import * as authApi from "@/api/auth";
import type { AuthUser } from "@/types/api";

export type { AuthUser };

export function useAuth() {
  const { accessToken, setTokens, clearTokens } = useAuthStore();
  const { user, setUser, clearUser } = useUserStore();

  const isAuthenticated = !!accessToken && !!user;

  /** 카카오 인가 코드로 로그인 */
  const loginWithKakao = async (code: string) => {
    const response = await authApi.kakaoLogin(code);
    setTokens(response.accessToken, response.refreshToken);
    setUser(response.user);
    return { user: response.user, isNewUser: response.isNewUser };
  };

  /** 이메일 로그인 */
  const loginWithEmail = async (email: string, password: string) => {
    const response = await authApi.emailLogin(email, password);
    setTokens(response.accessToken, response.refreshToken);
    setUser(response.user);
    return { user: response.user, isNewUser: response.isNewUser };
  };

  /** 이메일 회원가입 (휴대폰 인증 완료 후) */
  const signupWithEmail = async (email: string, password: string, phoneVerifiedToken: string) => {
    const response = await authApi.emailSignup(email, password, phoneVerifiedToken);
    setTokens(response.accessToken, response.refreshToken);
    setUser(response.user);
    return { user: response.user, isNewUser: response.isNewUser };
  };

  /** 카카오 신규 유저 약관 동의 후 회원가입 완료 */
  const register = async (agreeTerms: boolean, agreePrivacy: boolean) => {
    const response = await authApi.register(agreeTerms, agreePrivacy);
    setTokens(response.accessToken, response.refreshToken);
    setUser(response.user);
    return response.user;
  };

  /** 로그아웃 */
  const logout = async () => {
    try {
      await authApi.logout();
    } catch {
      // 로그아웃 API 실패해도 로컬 상태는 정리
    } finally {
      clearTokens();
      clearUser();
    }
  };

  return { user, isAuthenticated, loginWithKakao, loginWithEmail, signupWithEmail, register, logout };
}
